import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Users, 
  FileText, 
  Clock,
  CheckCircle,
  AlertCircle,
  BarChart3,
  PieChart as PieChartIcon,
  Activity,
  Building2,
  RefreshCw,
  Calendar
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const DashboardPage = () => {
  const [periodo, setPeriodo] = useState("mes-actual");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [recaudacionMensual, setRecaudacionMensual] = useState([]);
  const [distribucionImpuestos, setDistribucionImpuestos] = useState([]);
  const [actividadReciente, setActividadReciente] = useState([]);
  const [topContribuyentes, setTopContribuyentes] = useState([]);

  const fetchDashboardData = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const [statsRes, recaudacionRes, distribucionRes, actividadRes, topRes] = await Promise.all([
        axios.get(`${API}/api/dashboard/stats?periodo=${periodo}`, { headers }),
        axios.get(`${API}/api/dashboard/recaudacion-mensual`, { headers }),
        axios.get(`${API}/api/dashboard/distribucion-impuestos`, { headers }),
        axios.get(`${API}/api/dashboard/actividad-reciente?limite=8`, { headers }),
        axios.get(`${API}/api/dashboard/top-contribuyentes?limite=5`, { headers })
      ]);

      setStats(statsRes.data);
      setRecaudacionMensual(recaudacionRes.data);
      setDistribucionImpuestos(distribucionRes.data);
      setActividadReciente(actividadRes.data);
      setTopContribuyentes(topRes.data);
    } catch (error) {
      console.error("Error al cargar dashboard:", error);
      toast.error("Error al cargar datos del dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [periodo]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-VE', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  const getPeriodoLabel = () => {
    switch(periodo) {
      case "diario": return "Hoy";
      case "semanal": return "Esta Semana";
      case "mes-actual": return "Mes Actual";
      case "mes-anterior": return "Mes Anterior";
      case "anual": return "Este Año";
      default: return "Mes Actual";
    }
  };

  const COLORES_GRAFICO = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#6B7280"];

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Dashboard</h1>
          <p className="text-slate-500 mt-1">Resumen de métricas y estadísticas del sistema</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={periodo} onValueChange={setPeriodo}>
            <SelectTrigger className="w-[180px]" data-testid="periodo-selector">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Seleccionar período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="diario">Diario</SelectItem>
              <SelectItem value="semanal">Semanal</SelectItem>
              <SelectItem value="mes-actual">Mes Actual</SelectItem>
              <SelectItem value="mes-anterior">Mes Anterior</SelectItem>
              <SelectItem value="anual">Anual</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchDashboardData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* KPI Cards - Financieras */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Recaudado */}
        <Card className="border-l-4 border-l-emerald-500 hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Recaudado</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">
                  Bs. {formatCurrency(stats?.financieras?.total_recaudado)}
                </p>
                <div className="flex items-center mt-2">
                  {stats?.financieras?.variacion_porcentual >= 0 ? (
                    <Badge className="bg-emerald-100 text-emerald-700 flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      +{stats?.financieras?.variacion_porcentual}%
                    </Badge>
                  ) : (
                    <Badge className="bg-red-100 text-red-700 flex items-center gap-1">
                      <TrendingDown className="w-3 h-3" />
                      {stats?.financieras?.variacion_porcentual}%
                    </Badge>
                  )}
                  <span className="text-xs text-slate-400 ml-2">vs período anterior</span>
                </div>
              </div>
              <div className="p-3 bg-emerald-100 rounded-full">
                <DollarSign className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Comprobantes Pagados */}
        <Card className="border-l-4 border-l-blue-500 hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Comprobantes Pagados</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">
                  {stats?.financieras?.comprobantes_pagados || 0}
                </p>
                <p className="text-xs text-slate-400 mt-2">{getPeriodoLabel()}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <CheckCircle className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pendientes */}
        <Card className="border-l-4 border-l-amber-500 hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Pendientes por Cobrar</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">
                  {stats?.financieras?.liquidaciones_pendientes || 0}
                </p>
                <p className="text-xs text-amber-600 mt-2">
                  Bs. {formatCurrency(stats?.financieras?.monto_pendiente)}
                </p>
              </div>
              <div className="p-3 bg-amber-100 rounded-full">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contribuyentes */}
        <Card className="border-l-4 border-l-violet-500 hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Contribuyentes</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">
                  {stats?.operativas?.total_contribuyentes || 0}
                </p>
                <p className="text-xs text-slate-400 mt-2">
                  {stats?.operativas?.contribuyentes_con_lae || 0} con L.A.E.
                </p>
              </div>
              <div className="p-3 bg-violet-100 rounded-full">
                <Users className="w-6 h-6 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* KPI Cards - Operativas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500 rounded-lg">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-blue-600 font-medium">Fichas Catastrales</p>
                <p className="text-xl font-bold text-blue-800">{stats?.operativas?.fichas_catastrales || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500 rounded-lg">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-emerald-600 font-medium">Solvencias Prop. Inmob. Emitidas</p>
                <p className="text-xl font-bold text-emerald-800">{stats?.operativas?.solvencias_propiedad_emitidas || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500 rounded-lg">
                <AlertCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-amber-600 font-medium">Boletines Pendientes Catastro</p>
                <p className="text-xl font-bold text-amber-800">{stats?.operativas?.boletines_pendientes || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500 rounded-lg">
                <AlertCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-orange-600 font-medium">Boletines Pendientes Desarrollo Urbano</p>
                <p className="text-xl font-bold text-orange-800">{stats?.operativas?.boletines_pendientes_desarrollo_urbano || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-violet-50 to-violet-100 border-violet-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-500 rounded-lg">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-violet-600 font-medium">Expedientes Digitales</p>
                <p className="text-xl font-bold text-violet-800">{stats?.operativas?.expedientes_digitales || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Gráfico de Barras - Recaudación Mensual */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  Recaudación Mensual
                </CardTitle>
                <CardDescription>Últimos 12 meses</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={recaudacionMensual}>
                <defs>
                  <linearGradient id="colorRecaudacion" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis 
                  dataKey="mes" 
                  tick={{ fontSize: 11 }}
                  stroke="#94A3B8"
                />
                <YAxis 
                  tick={{ fontSize: 11 }}
                  stroke="#94A3B8"
                  tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip 
                  formatter={(value) => [`Bs. ${formatCurrency(value)}`, 'Recaudado']}
                  contentStyle={{ 
                    backgroundColor: '#1E293B',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff'
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="total" 
                  stroke="#3B82F6" 
                  strokeWidth={2}
                  fillOpacity={1} 
                  fill="url(#colorRecaudacion)" 
                />
                <Line 
                  type="monotone" 
                  dataKey="total" 
                  stroke="#3B82F6" 
                  strokeWidth={2}
                  dot={{ fill: '#3B82F6', strokeWidth: 2 }}
                  activeDot={{ r: 6 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Gráfico Circular - Distribución */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChartIcon className="w-5 h-5 text-emerald-600" />
              Distribución por Tipo
            </CardTitle>
            <CardDescription>Recaudación por concepto</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={distribucionImpuestos}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="total"
                  nameKey="nombre"
                >
                  {distribucionImpuestos.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORES_GRAFICO[index % COLORES_GRAFICO.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => [`Bs. ${formatCurrency(value)}`, '']}
                  contentStyle={{ 
                    backgroundColor: '#1E293B',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-2 mt-4">
              {distribucionImpuestos.slice(0, 4).map((item, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: COLORES_GRAFICO[index % COLORES_GRAFICO.length] }}
                    />
                    <span className="text-slate-600 truncate max-w-[120px]">{item.nombre}</span>
                  </div>
                  <span className="font-medium text-slate-800">{item.cantidad}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tablas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Actividad Reciente */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-amber-600" />
              Actividad Reciente
            </CardTitle>
            <CardDescription>Últimos movimientos del sistema</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {actividadReciente.length === 0 ? (
                <p className="text-center text-slate-400 py-8">No hay actividad reciente</p>
              ) : (
                actividadReciente.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${item.estatus === 'Pagado' ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                        {item.estatus === 'Pagado' ? (
                          <CheckCircle className="w-4 h-4 text-emerald-600" />
                        ) : (
                          <Clock className="w-4 h-4 text-amber-600" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-700 truncate max-w-[200px]">
                          {item.descripcion}
                        </p>
                        <p className="text-xs text-slate-400">
                          {item.fecha ? new Date(item.fecha).toLocaleDateString('es-VE') : '-'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-slate-800">
                        Bs. {formatCurrency(item.monto)}
                      </p>
                      <Badge variant={item.estatus === 'Pagado' ? 'default' : 'secondary'} className="text-xs">
                        {item.estatus}
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Contribuyentes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-violet-600" />
              Top Contribuyentes
            </CardTitle>
            <CardDescription>Por monto total recaudado</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {topContribuyentes.length === 0 ? (
                <p className="text-center text-slate-400 py-8">No hay datos de contribuyentes</p>
              ) : (
                topContribuyentes.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                        index === 0 ? 'bg-amber-500' : 
                        index === 1 ? 'bg-slate-400' : 
                        index === 2 ? 'bg-amber-700' : 'bg-slate-300'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-700 truncate max-w-[180px]">
                          {item.nombre}
                        </p>
                        <p className="text-xs text-slate-400">{item.rif}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-slate-800">
                        Bs. {formatCurrency(item.total)}
                      </p>
                      <p className="text-xs text-slate-400">{item.pagos} pagos</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
